<script setup lang="ts">
import type { Comment, CommentSubmittedPayload } from '~/types/blog'
import { resolveAvatarUrl } from '~/composables/useAvatar'
import { emailHash } from '~/composables/useGravatar';

type TweetCommentItem = {
  ID: number
  CreatedAt?: string
  contentId: number
  parentId: number
  author: string
  emailHash?: string
  url?: string
  content: string
  emailNotify?: boolean
  replyToId?: number
  replyToAuthor?: string
  identity?: Comment['identity']
  children?: Comment[]
  localOnly?: boolean
}

const props = withDefaults(
  defineProps<{
    contentId: number
    commentCount?: number
    allowComment?: boolean
    gravatarMirror?: string
    autoExpand?: boolean
  }>(),
  {
    commentCount: 0,
    allowComment: true,
    gravatarMirror: '',
    autoExpand: false
  }
)

const emit = defineEmits<{ (e: 'submitted'): void }>()
const blogApi = useBlogApi()

const expanded = ref(false)
const showAll = ref(false)
const showComposer = ref(false)
const loading = ref(false)
const errorMessage = ref('')
const comments = ref<Comment[]>([])
const localComments = ref<TweetCommentItem[]>([])
const nextPage = ref(1)
const rootTotal = ref(0)
const localCommentCount = ref(Number(props.commentCount || 0))
const replyingTo = ref<TweetCommentItem | null>(null)
let tempCommentID = 0

const pageSize = 3

watch(
  () => props.commentCount,
  value => {
    localCommentCount.value = Number(value || 0)
  }
)

const nextTempCommentID = () => {
  tempCommentID -= 1
  return tempCommentID
}

const flattenComments = (list: Comment[]) => {
  const result: Comment[] = []
  const walk = (items: Comment[]) => {
    items.forEach(item => {
      result.push(item)
      if (item.children?.length) {
        walk(item.children)
      }
    })
  }
  walk(list)
  return result
}

const flatComments = computed<TweetCommentItem[]>(() => [...localComments.value, ...flattenComments(comments.value)])
const visibleComments = computed(() => (showAll.value ? flatComments.value : flatComments.value.slice(0, pageSize)))
const canToggle = computed(() => props.contentId > 0 && (props.allowComment || localCommentCount.value > 0))
const hasLoaded = computed(() => nextPage.value > 1)
const hasMoreRootPages = computed(() => comments.value.length < rootTotal.value)
const hasHiddenComments = computed(() => localCommentCount.value > visibleComments.value.length || hasMoreRootPages.value)
const composerParentId = computed(() => Number(replyingTo.value?.ID || 0))

const loadComments = async () => {
  if (loading.value || props.contentId <= 0 || (!hasMoreRootPages.value && hasLoaded.value)) {
    return
  }
  loading.value = true
  errorMessage.value = ''
  try {
    const data = await blogApi.getCommentTree(props.contentId, { page: nextPage.value, pageSize })
    comments.value = [...comments.value, ...(data.list || [])]
    rootTotal.value = Number(data.total || 0)
    nextPage.value += 1
  } catch (error) {
    errorMessage.value = (error as Error)?.message || '评论加载失败'
  } finally {
    loading.value = false
  }
}

const toggleExpanded = async () => {
  if (!canToggle.value) {
    return
  }
  if (expanded.value) {
    expanded.value = false
    return
  }
  if (!hasLoaded.value) {
    await loadComments()
  }
  expanded.value = true
}

const expandFromAnchor = async () => {
  if (!canToggle.value || expanded.value) {
    return
  }
  if (!hasLoaded.value) {
    await loadComments()
  }
  expanded.value = true
}

watch(
  () => props.autoExpand,
  value => {
    if (value) {
      void expandFromAnchor()
    }
  },
  { immediate: true }
)

const showAllComments = async () => {
  showAll.value = true
  if (!hasLoaded.value || hasMoreRootPages.value) {
    await loadComments()
  }
}

const startRootComment = () => {
  replyingTo.value = null
  showComposer.value = true
}

const startReply = (item: TweetCommentItem) => {
  replyingTo.value = item
  showComposer.value = true
}

const clearReply = () => {
  replyingTo.value = null
}

const buildLocalComment = (payload: CommentSubmittedPayload): TweetCommentItem => ({
  ID: Number(payload.ID || 0) || nextTempCommentID(),
  CreatedAt: payload.createdAt,
  contentId: props.contentId,
  parentId: payload.parentId || 0,
  author: payload.author,
  emailHash: payload.emailHash || emailHash(payload.email || ''),
  url: payload.url,
  content: payload.content,
  emailNotify: payload.emailNotify,
  replyToId: composerParentId.value,
  replyToAuthor: replyingTo.value?.author || '',
  children: [],
  localOnly: true
})

const onSubmitted = (payload: CommentSubmittedPayload) => {
  localComments.value.splice(0, 0, buildLocalComment(payload))
  localCommentCount.value += 1
  showAll.value = true
  showComposer.value = false
  replyingTo.value = null
  emit('submitted')
}

const avatarUrl = (item: TweetCommentItem) =>
  resolveAvatarUrl(item.emailHash, props.gravatarMirror)

const formatTime = (value?: string) => {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const composeAreaRef = ref<HTMLElement | null>(null)
let composeResizeObserver: ResizeObserver | null = null
let composeMeasureFrame: number | null = null
let panelTransitioning = false

const clearComposeObserver = () => {
  composeResizeObserver?.disconnect()
  composeResizeObserver = null
}

const clearComposeMeasureFrame = () => {
  if (composeMeasureFrame !== null && import.meta.client) {
    cancelAnimationFrame(composeMeasureFrame)
  }
  composeMeasureFrame = null
}

const clearComposeLayout = () => {
  clearComposeMeasureFrame()
  clearComposeObserver()
}

const getActiveComposeElement = () => {
  const area = composeAreaRef.value
  if (!area) {
    return null
  }

  const candidates = Array.from(area.children).filter(
    child => child instanceof HTMLElement && !child.classList.contains('tweet-comment-compose-leave-active')
  ) as HTMLElement[]
  return candidates[candidates.length - 1] || null
}

const updateComposeHeight = () => {
  const area = composeAreaRef.value
  const activeElement = getActiveComposeElement()
  if (!area || !activeElement || !expanded.value || !area.getClientRects().length) {
    return
  }

  const nextHeight = Math.ceil(Math.max(activeElement.scrollHeight, activeElement.getBoundingClientRect().height))
  if (nextHeight <= 0) {
    return
  }

  area.style.height = `${nextHeight}px`
}

const scheduleComposeHeightUpdate = () => {
  if (!import.meta.client) {
    return
  }

  clearComposeMeasureFrame()
  composeMeasureFrame = requestAnimationFrame(() => {
    composeMeasureFrame = null
    updateComposeHeight()
  })
}

const observeComposeHeight = () => {
  if (!import.meta.client) {
    return
  }

  clearComposeObserver()
  const activeElement = getActiveComposeElement()
  if (!activeElement || typeof ResizeObserver === 'undefined') {
    return
  }

  composeResizeObserver = new ResizeObserver(() => scheduleComposeHeightUpdate())
  composeResizeObserver.observe(activeElement)
  const content = activeElement.firstElementChild
  if (content instanceof HTMLElement) {
    composeResizeObserver.observe(content)
  }
}

const refreshComposeLayout = async () => {
  await nextTick()
  if (!expanded.value) {
    clearComposeLayout()
    return
  }
  scheduleComposeHeightUpdate()
  if (panelTransitioning) {
    return
  }
  observeComposeHeight()
}

const beforeEnter = (element: Element) => {
  panelTransitioning = true
  clearComposeLayout()
  updateComposeHeight()
  const panel = element as HTMLElement
  panel.style.height = '0'
  panel.style.opacity = '0'
}

const enter = (element: Element) => {
  const panel = element as HTMLElement
  updateComposeHeight()
  void panel.offsetHeight
  panel.style.height = `${panel.scrollHeight}px`
  panel.style.opacity = '1'
}

const afterEnter = (element: Element) => {
  panelTransitioning = false
  const panel = element as HTMLElement
  panel.style.height = 'auto'
  panel.style.opacity = ''
  void refreshComposeLayout()
}

const beforeLeave = (element: Element) => {
  panelTransitioning = true
  clearComposeLayout()
  const panel = element as HTMLElement
  panel.style.height = `${panel.scrollHeight}px`
  panel.style.opacity = '1'
}

const leave = (element: Element) => {
  const panel = element as HTMLElement
  void panel.offsetHeight
  panel.style.height = '0'
  panel.style.opacity = '0'
}

const afterLeave = (element: Element) => {
  panelTransitioning = false
  const panel = element as HTMLElement
  panel.style.height = ''
  panel.style.opacity = ''
  clearComposeLayout()
}

watch(showComposer, () => {
  void refreshComposeLayout()
})

watch(replyingTo, () => {
  void refreshComposeLayout()
})

watch(expanded, value => {
  if (value) {
    void refreshComposeLayout()
  } else {
    clearComposeLayout()
  }
})

onMounted(() => {
  void refreshComposeLayout()
})

const beforeComposeEnter = () => {
  scheduleComposeHeightUpdate()
}

const enterCompose = () => {
  scheduleComposeHeightUpdate()
}

const afterComposeEnter = () => {
  void refreshComposeLayout()
}

const beforeComposeLeave = () => {
  scheduleComposeHeightUpdate()
}

const leaveCompose = () => {
  scheduleComposeHeightUpdate()
}

const afterComposeLeave = () => {
  void refreshComposeLayout()
}

onBeforeUnmount(() => clearComposeLayout())

</script>

<template>
  <slot
    :expanded="expanded"
    :comment-count="localCommentCount"
    :can-toggle="canToggle"
    :loading="loading"
    :toggle-expanded="toggleExpanded"
  />

  <Transition
    name="tweet-comments"
    @before-enter="beforeEnter"
    @enter="enter"
    @after-enter="afterEnter"
    @before-leave="beforeLeave"
    @leave="leave"
    @after-leave="afterLeave"
  >
    <div v-show="expanded" class="tweet-comments-panel" :aria-hidden="!expanded" :inert="!expanded">
      <div class="tweet-comments-inner">
        <div v-if="loading && !flatComments.length" class="tweet-comment-state loading">评论加载中...</div>
        <p v-else-if="errorMessage" class="tweet-comment-error">{{ errorMessage }}</p>
        <template v-else>
          <TransitionGroup v-if="visibleComments.length" name="tweet-comment-list" tag="div" class="tweet-comment-list">
            <article v-for="comment in visibleComments" :key="comment.ID" class="tweet-comment-item">
              <div class="tweet-comment-person">
                <span class="tweet-comment-avatar-frame">
                  <BlogImage class="tweet-comment-avatar" :src="avatarUrl(comment)" :alt="comment.author || 'avatar'" />
                  <BlogCommentVerifyIcon :identity="comment.identity" size="small" />
                </span>
                <BlogUiTooltip :content="comment.author || '匿名用户'">
                  <span class="tweet-comment-author">{{ comment.author || '匿名用户' }}</span>
                </BlogUiTooltip>
              </div>
              <div class="tweet-comment-main">
                <p class="tweet-comment-content">
                  <span v-if="comment.replyToAuthor" class="tweet-comment-mention">@{{ comment.replyToAuthor }}</span>{{ comment.content }}
                </p>
                <div class="tweet-comment-side">
                  <time v-if="formatTime(comment.CreatedAt)">
                    <i class="fa-regular fa-clock" aria-hidden="true" />
                    <span>{{ formatTime(comment.CreatedAt) }}</span>
                  </time>
                  <div class="tweet-comment-actions">
                    <button v-if="allowComment && !comment.localOnly" type="button" class="tweet-comment-reply-btn" @click="startReply(comment)">
                      <i class="fa-solid fa-reply" aria-hidden="true" />
                      <span>回复</span>
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </TransitionGroup>
          <div v-else class="tweet-comment-empty">
            <span>还没有评论，写下第一句吧</span>
          </div>

          <div v-if="hasHiddenComments" class="tweet-comment-more-row">
            <Transition name="tweet-comment-more" mode="out-in">
              <button v-if="!showAll" key="show-all" type="button" class="tweet-comment-more" @click="showAllComments">
                查看更多
              </button>
              <button v-else-if="hasMoreRootPages" key="load-more" type="button" class="tweet-comment-more" :disabled="loading" @click="loadComments">
                {{ loading ? '加载中...' : '查看更多' }}
              </button>
            </Transition>
          </div>

          <div v-if="allowComment" ref="composeAreaRef" class="tweet-comment-compose-area">
            <Transition
              name="tweet-comment-compose"
              @before-enter="beforeComposeEnter"
              @enter="enterCompose"
              @after-enter="afterComposeEnter"
              @before-leave="beforeComposeLeave"
              @leave="leaveCompose"
              @after-leave="afterComposeLeave"
            >
              <button v-if="!showComposer" key="entry" type="button" class="tweet-comment-entry" @click="startRootComment">
                <span class="tweet-comment-entry-icon"><i class="fa-regular fa-pen-to-square" aria-hidden="true" /></span>
                <span class="tweet-comment-entry-text">写下评论...</span>
                <i class="fa-solid fa-chevron-right tweet-comment-entry-arrow" aria-hidden="true" />
              </button>
              <div v-else key="composer" class="tweet-comment-composer">
                <div class="tweet-comment-composer-inner">
                  <BlogCommentForm
                    :content-id="contentId"
                    :parent-id="composerParentId"
                    :reply-author="replyingTo ? (replyingTo.author || '匿名用户') : ''"
                    variant="compact"
                    @cancel-reply="clearReply"
                    @submitted="onSubmitted"
                  />
                </div>
              </div>
            </Transition>
          </div>
        </template>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.tweet-comments-panel {
  --tweet-comment-divider-bg: color-mix(in srgb, var(--line) 72%, transparent);
  height: auto;
  opacity: 1;
  overflow: hidden;
}

.tweet-comments-enter-active {
  transition:
    height 0.3s cubic-bezier(0, 0, 0.2, 1),
    opacity 0.2s cubic-bezier(0, 0, 0.2, 1);
}

.tweet-comments-leave-active {
  transition:
    height 0.2s cubic-bezier(0.4, 0, 1, 1),
    opacity 0.12s cubic-bezier(0.4, 0, 1, 1);
}

.tweet-comments-inner {
  min-height: 0;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--tweet-comment-divider-bg);
  color: var(--text);
  display: grid;
  gap: 9px;
  overflow: hidden;
}

.tweet-comment-list {
  display: grid;
  gap: 8px;
}

.tweet-comment-item {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 9px;
  align-items: start;
}

.tweet-comment-person {
  min-width: 0;
  display: grid;
  justify-items: center;
  gap: 4px;
}

.tweet-comment-person :deep(.ui-tooltip-wrap) {
  max-width: 40px;
}

.tweet-comment-avatar-frame {
  position: relative;
  display: block;
  width: 30px;
  height: 30px;
}

.tweet-comment-avatar {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: block;
  object-fit: cover;
  background: color-mix(in srgb, var(--brand) 12%, var(--bg-soft));
}

.tweet-comment-author {
  max-width: 40px;
  color: var(--text-soft);
  font-size: 11px;
  line-height: 1.25;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tweet-comment-main {
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 10px;
  align-items: start;
  padding: 7px 10px 8px;
  border-radius: var(--radius);
  background: var(--editor-bg);
}

.tweet-comment-side {
  grid-column: 2;
  grid-row: 1;
  display: grid;
  grid-template-rows: auto 1fr;
  justify-items: end;
  align-self: stretch;
  gap: 4px;
  color: var(--text-soft);
  font-size: 12px;
  line-height: 1.35;
  white-space: nowrap;
}

.tweet-comment-actions {
  display: inline-flex;
  align-items: center;
  align-self: end;
  gap: 6px;
  max-width: 100%;
}

.tweet-comment-side time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.tweet-comment-content {
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
  margin: 0;
  white-space: pre-wrap;
  color: var(--text);
  font-size: 13px;
  line-height: 1.65;
}

.tweet-comment-mention {
  margin-right: 5px;
  color: var(--brand);
}

.tweet-comment-reply-btn {
  border: 0;
  padding: 0;
  color: var(--brand);
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  cursor: pointer;
}

.tweet-comment-reply-btn i {
  font-size: 11px;
}

.tweet-comment-state,
.tweet-comment-error {
  margin: 0;
  color: var(--text-soft);
  font-size: 13px;
}

.tweet-comment-state.loading {
  min-height: 78px;
  display: flex;
  align-items: center;
}

.tweet-comment-empty {
  min-height: 54px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: var(--radius);
  padding: 9px 10px;
  text-align: center;
  color: var(--text-soft);
  font-size: 13px;
  background: var(--empty-bg);
}

.tweet-comment-error {
  color: #d64545;
}

.tweet-comment-more-row {
  display: flex;
}

.tweet-comment-more {
  border: 0;
  padding: 0;
  color: var(--brand);
  background: transparent;
  font-size: 13px;
  cursor: pointer;
}

.tweet-comment-compose-area {
  display: grid;
  align-items: start;
  overflow: hidden;
  transition: height 0.24s cubic-bezier(0.22, 1, 0.36, 1);
}

.tweet-comment-entry {
  grid-area: 1 / 1;
  width: 100%;
  min-height: 38px;
  border: 1px solid transparent;
  border-radius: var(--radius);
  padding: 6px 9px;
  color: var(--text-soft);
  background: var(--editor-bg);
  cursor: text;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  text-align: left;
  transition:
    background 0.18s ease,
    color 0.18s ease;
}

.tweet-comment-entry:hover,
.tweet-comment-entry:focus-visible {
  color: var(--brand);
  background: color-mix(in srgb, var(--brand-soft) 24%, var(--bg) 76%);
}

.tweet-comment-entry-icon {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: color-mix(in srgb, var(--text-soft) 82%, var(--brand) 18%);
  transition: color 0.18s ease;
}

.tweet-comment-entry-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}

.tweet-comment-entry-arrow {
  color: color-mix(in srgb, var(--text-soft) 82%, var(--brand) 18%);
  font-size: 13px;
  transition: color 0.18s ease;
}

.tweet-comment-entry:hover .tweet-comment-entry-icon,
.tweet-comment-entry:focus-visible .tweet-comment-entry-icon,
.tweet-comment-entry:hover .tweet-comment-entry-arrow,
.tweet-comment-entry:focus-visible .tweet-comment-entry-arrow {
  color: var(--brand);
}

.tweet-comment-list-enter-active,
.tweet-comment-list-leave-active,
.tweet-comment-more-enter-active,
.tweet-comment-more-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.tweet-comment-compose-enter-active,
.tweet-comment-compose-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.tweet-comment-list-enter-from,
.tweet-comment-list-leave-to,
.tweet-comment-more-enter-from,
.tweet-comment-more-leave-to,
.tweet-comment-compose-enter-from,
.tweet-comment-compose-leave-to {
  opacity: 0;
  transform: translateY(-2px);
}

.tweet-comment-composer {
  grid-area: 1 / 1;
  overflow: hidden;
}

.tweet-comment-composer-inner {
  display: grid;
  padding-top: 8px;
}

@media (prefers-reduced-motion: reduce) {
  .tweet-comments-enter-active,
  .tweet-comments-leave-active,
  .tweet-comment-list-enter-active,
  .tweet-comment-list-leave-active,
  .tweet-comment-more-enter-active,
  .tweet-comment-more-leave-active,
  .tweet-comment-compose-area,
  .tweet-comment-compose-enter-active,
  .tweet-comment-compose-leave-active {
    transition: none;
  }
}
</style>
